import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import {
  Button,
  DataTable,
  Field,
  Input,
  Kpi,
  Pager,
  Select,
  StateBlock,
  type Column,
} from '@w7/ui'
import { useGetSiteUrl } from '@/api/hooks/member/useGetSiteUrl'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { CmPanel } from '@/features/catalog/CmPanel'
import { isoDay } from '@/lib/format'
import './vendor-dashboard.css'

/** Lab Config.maxRow for Card Game Vendor Dashboard. */
const PAGE_SIZE = 50

type OperatorType = 'INR' | 'POINTS'

type Row = {
  siteUrl: string
  createdAt: string
  playerName: string
  eventName: string
  marketId: string
  type: string
  currency: string
  netAmount: number
  settlementId: string
}

type Applied = {
  from: string
  to: string
  operator: OperatorType
  siteUrl: string
  run: number
}

/**
 * Vendor Dashboard → Card Game from Strix BQ settlement.settlement
 * (CM-API `POST /v1/vendor/card-game/bq-detail` + `Member/getSiteUrl` domains).
 */
export function CardGamePage() {
  const today = isoDay(new Date())
  const [operator, setOperator] = useState<OperatorType>('INR')
  const [domain, setDomain] = useState('all')
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [applied, setApplied] = useState<Applied>({
    from: today,
    to: today,
    operator: 'INR',
    siteUrl: '',
    run: 0,
  })
  const [page, setPage] = useState(1)

  const domainsQ = useGetSiteUrl()

  const domainOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...(domainsQ.data ?? []).map((d) => ({
        value: d._id,
        label: d.name || d._id,
      })),
    ],
    [domainsQ.data],
  )

  const q = useQuery({
    // `run` bumps on every Get Detail click so same filters still refetch.
    queryKey: [
      'cm',
      'vendor',
      'card-game',
      'bq-detail',
      applied.from,
      applied.to,
      applied.operator,
      applied.siteUrl,
      applied.run,
      page,
      PAGE_SIZE,
    ],
    queryFn: () =>
      cmApi.cardGameBqDetail(authHeaders(), {
        fromDate: applied.from,
        toDate: applied.to,
        operatorType: applied.operator,
        ...(applied.siteUrl ? { siteUrl: applied.siteUrl } : {}),
        page,
        pageSize: PAGE_SIZE,
      }),
  })

  const rows = q.data?.rows ?? []
  const total = q.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const finalTotal = q.data?.finalTotal ?? 0

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        id: 'n',
        header: 'No.',
        width: 56,
        numeric: true,
        cell: (_r, i) => String((page - 1) * PAGE_SIZE + i + 1),
      },
      {
        id: 'createdAt',
        header: 'Created At',
        cell: (r) =>
          r.createdAt ? new Date(r.createdAt).toLocaleString() : '—',
      },
      { id: 'site', header: 'Site', cell: (r) => r.siteUrl || '—' },
      { id: 'player', header: 'Player Name', cell: (r) => r.playerName || '—' },
      { id: 'event', header: 'Event Name', cell: (r) => r.eventName || '—' },
      { id: 'market', header: 'Market Id', cell: (r) => r.marketId || '—' },
      { id: 'type', header: 'Type', cell: (r) => r.type || '—' },
      {
        id: 'net',
        header: 'Net Amount',
        numeric: true,
        cell: (r) => {
          const n = r.netAmount
          const cls = n > 0 ? 'cm-money-pos' : n < 0 ? 'cm-money-neg' : undefined
          return <span className={cls}>{n.toFixed(2)}</span>
        },
      },
    ],
    [page],
  )

  return (
    <CmPanel
      title="Card Game"
      subtitle="Vendor Dashboard — Card Game from BQ settlement (bq-detail)"
      wide
      actions={
        <Button
          type="button"
          variant="secondary"
          busy={q.isFetching}
          onClick={() => void q.refetch()}
        >
          Refresh
        </Button>
      }
    >
      <div className="cm-vendor-filters">
        <Field label="Select Operator Type" required>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={operator}
              onChange={(e) => setOperator(e.target.value as OperatorType)}
              options={[
                { value: 'INR', label: 'INR' },
                { value: 'POINTS', label: 'POINTS' },
              ]}
            />
          )}
        </Field>
        <Field label="Select Domain">
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              options={domainOptions}
            />
          )}
        </Field>
        <Field label="From Date" required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="date"
              required
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          )}
        </Field>
        <Field label="To Date" required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="date"
              required
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          )}
        </Field>
        <div className="cm-vendor-filters-actions">
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              setPage(1)
              setApplied((prev) => ({
                from: fromDate,
                to: toDate,
                operator,
                siteUrl: domain === 'all' ? '' : domain,
                run: prev.run + 1,
              }))
            }}
          >
            Get Detail
          </Button>
        </div>
        <div className="cm-vendor-filters-kpi">
          <Kpi
            label="Final Total"
            value={q.isLoading && !q.data ? '—' : finalTotal.toFixed(2)}
            meta="Sum of player net for range"
          />
        </div>
      </div>

      {q.isLoading && !rows.length ? (
        <StateBlock icon="sport" title="Loading Card Game…">
          Reading Card Game BQ settlements
        </StateBlock>
      ) : null}

      {!q.isLoading && rows.length === 0 ? (
        <StateBlock icon="search" title="No data found">
          No Card Game BQ settlements for these filters.
        </StateBlock>
      ) : null}

      {rows.length > 0 ? (
        <>
          <DataTable
            label="Card Game settlements"
            columns={columns}
            rows={rows}
            rowKey={(r, i) => `${r.settlementId}-${i}`}
          />
          <Pager
            page={page}
            pageCount={pageCount}
            total={total}
            pageSize={PAGE_SIZE}
            onChange={setPage}
          />
        </>
      ) : null}
    </CmPanel>
  )
}
